/**
 * Progresso composto do brinde (mesma regra de gift-system.js).
 * Valor preenche até valueShare%; o produto gatilho completa o restante até 100%.
 * A fatia do gatilho enche proporcionalmente à quantidade atingida quando
 * options.triggerProgress (saída de resolveTriggerProgress do núcleo, com
 * required/matched/satisfied) é informado. Sem ele, cai na forma booleana
 * anterior (options.hasTrigger) e produz exatamente o resultado de antes,
 * porque existe chamada em produção que ainda passa só o booleano.
 */
function computeGiftProgressPercent(cartTotal, minValue, options) {
  const opts = options || {};
  const valueShare = typeof opts.valueShare === 'number' ? opts.valueShare : 80;
  const triggerShare = typeof opts.triggerShare === 'number' ? opts.triggerShare : 20;
  const needsTrigger = Boolean(opts.needsTrigger);

  if (!minValue || minValue <= 0) {
    return 0;
  }

  const valueRatio = Math.min(Math.max(Number(cartTotal) / minValue, 0), 1);

  if (!needsTrigger) {
    return Math.min(100, Math.round(valueRatio * 100));
  }

  const progress = opts.triggerProgress;
  let triggerRatio;
  if (progress && typeof progress.matched === 'number' && progress.required > 0) {
    triggerRatio = Math.min(Math.max(progress.matched / progress.required, 0), 1);
  } else if (progress) {
    triggerRatio = progress.satisfied ? 1 : 0;
  } else {
    triggerRatio = opts.hasTrigger ? 1 : 0;
  }

  return Math.min(
    100,
    Math.round(valueRatio * valueShare + triggerRatio * triggerShare)
  );
}

if (typeof window !== 'undefined' && typeof window.computeGiftProgressPercent !== 'function') {
  window.computeGiftProgressPercent = computeGiftProgressPercent;
}

/**
 * Resolve a copy da barra por estado (mesma regra de gift-system.js).
 */
function resolveGiftProgressMessage(state, config, options) {
  const opts = options || {};
  const remaining = typeof opts.remaining === 'number' ? opts.remaining : 0;
  const missing = typeof opts.missing === 'number' ? opts.missing : 0;
  const levelNumber = opts.levelNumber != null ? opts.levelNumber : '';
  const formatCurrency = typeof opts.formatCurrency === 'function'
    ? opts.formatCurrency
    : (v) => String(v);

  const fill = (template) => String(template || '')
    .replace(/\{valor\}/g, formatCurrency(Math.max(remaining, 0)))
    .replace(/\{nivel\}/g, String(levelNumber))
    .replace(/\{faltam\}/g, String(Math.max(missing, 0)));

  if (state === 'unlocked') {
    return fill(config && config.messageUnlockedSingle
      ? config.messageUnlockedSingle
      : '🎁 Parabéns! Você ganhou um brinde!');
  }

  if (state === 'trigger_missing') {
    const productFallback = 'Adicione um dos produtos especiais para desbloquear o brinde!';
    let template = (config && config.messageTriggerMissing) || productFallback;

    // Template com {faltam} fala de quantidade de produto, não de dinheiro:
    // nunca é trocado pela heurística abaixo, porque {faltam} é exatamente o
    // marcador que este estado existe para preencher (valor já atingido,
    // falta gatilho) — o momento em que a heurística de "R$" dispararia hoje.
    const hasQuantityMarker = /\{faltam\}/i.test(template);

    const looksLikeMoneyLeft = !hasQuantityMarker && (
      /\{valor\}/i.test(template) ||
      /falta\s+(apenas\s+)?r\s*\$/i.test(template) ||
      (/falta\s+apenas/i.test(template) && /r\$|reais|valor/i.test(template))
    );

    if (looksLikeMoneyLeft && remaining <= 0) {
      const progressMsg = (config && config.messageProgress) || '';
      if (progressMsg && !/\{valor\}/i.test(progressMsg)) {
        template = progressMsg;
      } else {
        template = productFallback;
      }
    }

    return fill(template);
  }

  return fill(
    (config && config.messageProgress) || 'Falta apenas {valor} para ganhar um brinde!'
  );
}

if (typeof window !== 'undefined' && typeof window.resolveGiftProgressMessage !== 'function') {
  window.resolveGiftProgressMessage = resolveGiftProgressMessage;
}

class GiftProgressComponent extends HTMLElement {
  constructor() {
    super();
    this.progressBar = this.querySelector('[data-gift-progress-meter]');
    this.progressText = this.progressBar?.querySelector('.text');
    this.messageEl = this.querySelector('[data-gift-message]');
    this.progressContainer = this.querySelector('[data-gift-progress]');
  }

  connectedCallback() {
    // Re-bind DOM: constructor pode rodar antes dos filhos existirem
    this.progressBar = this.querySelector('[data-gift-progress-meter]');
    this.progressText = this.progressBar ? this.progressBar.querySelector('.text') : null;
    this.messageEl = this.querySelector('[data-gift-message]');
    this.progressContainer = this.querySelector('[data-gift-progress]');

    // Aplicar cores personalizadas
    this.applyCustomColors();

    // Com escolha ligada, o coordenador dona o snapshot (sem fetch próprio)
    if (window.giftSystemConfig?.choiceEnabled === true) {
      this.connectChoiceCoordinatorPresenter();
      return;
    }

    // Armazenar referência do listener para poder remover depois
    this.boundUpdateProgress = this.updateProgress.bind(this);

    // Escutar eventos de atualização do carrinho
    document.addEventListener('cart-update', this.boundUpdateProgress);

    // Atualizar na inicialização com delay para garantir que config está carregado
    setTimeout(() => this.updateProgress(), 500);
  }

  connectChoiceCoordinatorPresenter() {
    const coordinator = window.GiftChoiceCoordinator;

    if (
      !coordinator ||
      coordinator.__giftChoiceCoordinator !== true ||
      typeof coordinator.registerPresenter !== 'function'
    ) {
      // Coordenador ainda não subiu: fallback legado para não deixar a barra morta
      setTimeout(() => {
        if (window.giftSystemConfig?.choiceEnabled === true) {
          const ok = this.connectChoiceCoordinatorPresenter();
          if (!ok && !this.boundUpdateProgress) {
            this.boundUpdateProgress = this.updateProgress.bind(this);
            document.addEventListener('cart-update', this.boundUpdateProgress);
            this.updateProgress();
          }
        }
      }, 300);
      return false;
    }

    coordinator.registerPresenter(this);
    this.choiceCoordinator = coordinator;
    return true;
  }

  /**
   * Aplica cores personalizadas via CSS variables
   */
  applyCustomColors() {
    const config = window.giftSystemConfig;
    if (!config || !config.colors) {
      return;
    }

    const colors = config.colors;

    // Aplicar CSS variables no elemento raiz (haloCalculatorGift)
    if (this.style) {
      this.style.setProperty('--gift-progress-color-30', colors.progress30 || '#a78bfa');
      this.style.setProperty('--gift-progress-color-60', colors.progress60 || '#8b5cf6');
      this.style.setProperty('--gift-progress-color-100', colors.progress100 || '#7c3aed');
      this.style.setProperty('--gift-progress-color-unlocked', colors.progressUnlocked || '#7c3aed');
      this.style.setProperty('--gift-progress-bg-color', colors.progressBg || '#f0f0f0');
      this.style.setProperty('--gift-progress-text-color', colors.progressText || '#ffffff');
      this.style.setProperty('--gift-message-color-default', colors.messageDefault || '#666666');
      this.style.setProperty('--gift-message-color-30', colors.message30 || '#a78bfa');
      this.style.setProperty('--gift-message-color-60', colors.message60 || '#8b5cf6');
      this.style.setProperty('--gift-message-color-100', colors.message100 || '#7c3aed');
      this.style.setProperty('--gift-message-color-unlocked', colors.messageUnlocked || '#7c3aed');
    }

    // Também aplicar no progress container se existir
    if (this.progressContainer && this.progressContainer.style) {
      this.progressContainer.style.setProperty('--gift-progress-color-30', colors.progress30 || '#a78bfa');
      this.progressContainer.style.setProperty('--gift-progress-color-60', colors.progress60 || '#8b5cf6');
      this.progressContainer.style.setProperty('--gift-progress-color-100', colors.progress100 || '#7c3aed');
      this.progressContainer.style.setProperty('--gift-progress-color-unlocked', colors.progressUnlocked || '#7c3aed');
      this.progressContainer.style.setProperty('--gift-progress-bg-color', colors.progressBg || '#f0f0f0');
      this.progressContainer.style.setProperty('--gift-progress-text-color', colors.progressText || '#ffffff');
    }
  }

  async updateProgress() {
    try {
      // Reaplicar cores personalizadas (caso tenham mudado)
      this.applyCustomColors();

      // Usar configuração global do gift system
      const config = window.giftSystemConfig;

      if (!config || !config.enabled || !config.levels || config.levels.length === 0) {
        this.style.display = 'none';
        return;
      }

      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateProgressFromCart(cart);
    } catch (error) {
      // Erro silencioso
    }
  }

  renderGiftCoordinatorState({ cart } = {}) {
    this.applyCustomColors();
    if (!cart || !Array.isArray(cart.items)) {
      return;
    }
    // Recicla o pipeline de updateProgress com snapshot já resolvido
    this.updateProgressFromCart(cart);
  }

  updateProgressFromCart(cart) {
    // Re-bind DOM se necessário
    if (!this.progressBar || !this.messageEl) {
      this.progressBar = this.querySelector('[data-gift-progress-meter]');
      this.progressText = this.progressBar ? this.progressBar.querySelector('.text') : null;
      this.messageEl = this.querySelector('[data-gift-message]');
      this.progressContainer = this.querySelector('[data-gift-progress]');
    }

    const config = window.giftSystemConfig;
    if (!config || !config.enabled || !config.levels || config.levels.length === 0) {
      this.style.display = 'none';
      return;
    }


      // Mesma base da /cart: original_line_price em reais, excluindo brindes
      const cartTotal = this.getEligibleCartTotalReais(cart);

      // Nível atual/próximo do núcleo único (assets/gift-choice-core.js) —
      // mesma ordenação por valor mínimo que a barra da página /cart, o
      // card e o botão de produtos usam. Sem cópia local do critério.
      const { currentLevel, nextLevel } = this.resolveTargetLevel(config.levels, cartTotal);

      if (!nextLevel && !currentLevel) {
        this.style.display = 'none';
        return;
      }

      this.style.display = 'block';

      if (currentLevel && !nextLevel) {
        const needsTrigger = currentLevel.triggerProducts && currentLevel.triggerProducts.length > 0;
        const triggerProgress = this.resolveTriggerProgress(currentLevel, cart);
        const isFullyUnlocked = this.checkLevelEligibility(currentLevel, cart);
        const progress = computeGiftProgressPercent(cartTotal, currentLevel.minValue, {
          needsTrigger,
          triggerProgress
        });

        const formatCurrency = (v) => v.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        });

        if (isFullyUnlocked) {
          const message = resolveGiftProgressMessage('unlocked', config, {
            remaining: 0,
            levelNumber: currentLevel.levelNumber,
            formatCurrency
          });
          this.updateUI(100, message, 'progress-free', cart);
        } else {
          // Valor OK, falta gatilho: fill proporcional (<100%), copy de
          // PRODUTO com a quantidade que falta (não de R$)
          const message = resolveGiftProgressMessage('trigger_missing', config, {
            remaining: 0,
            missing: triggerProgress.missing,
            levelNumber: currentLevel.levelNumber,
            formatCurrency
          });
          this.updateUI(progress, message, 'progress-100', cart);
        }
      } else if (nextLevel) {
        // nextLevel existe => cartTotal < minValue (só "falta valor")
        const needsTrigger = nextLevel.triggerProducts && nextLevel.triggerProducts.length > 0;
        const triggerProgress = this.resolveTriggerProgress(nextLevel, cart);
        const progress = computeGiftProgressPercent(cartTotal, nextLevel.minValue, {
          needsTrigger,
          triggerProgress
        });
        const remaining = Math.max(nextLevel.minValue - cartTotal, 0);
        const formatCurrency = (v) => v.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        });

        const message = resolveGiftProgressMessage('progress', config, {
          remaining,
          missing: triggerProgress.missing,
          levelNumber: nextLevel.levelNumber,
          formatCurrency
        });
        this.updateUI(progress, message, this.progressClassForPercent(progress), cart);
      } else {
        this.style.display = 'none';
      }
  }


  /**
   * Total elegível em reais: original_line_price (pré-desconto), ignora _is_gift.
   * Alinha quick cart com gift-system.js da página /cart.
   */
  getEligibleCartTotalReais(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }

    const cartTotalCents = cart.items.reduce((total, item) => {
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price || 0);
    }, 0);

    return cartTotalCents / 100;
  }

  progressClassForPercent(progress) {
    if (progress >= 100) return 'progress-100';
    if (progress >= 60) return 'progress-60';
    return 'progress-30';
  }

  updateUI(progress, message, progressClass, cart) {
    const safeProgress = Math.min(100, Math.max(0, Math.round(progress)));

    if (this.progressBar) {
      this.progressBar.style.width = `${safeProgress}%`;
    }

    if (this.progressText) {
      this.progressText.textContent = `${safeProgress}%`;
    }

    if (this.messageEl) {
      this.messageEl.textContent = message;
      this.messageEl.className = `message ${progressClass}`;
    }

    if (this.progressContainer) {
      this.progressContainer.className = `progress ${progressClass}`;
    }

    this.syncTriggerPickerButton(cart);
  }

  syncTriggerPickerButton(cart) {
    if (window.GiftTriggerPicker && typeof window.GiftTriggerPicker.syncButtons === 'function') {
      window.GiftTriggerPicker.syncButtons(cart);
      return;
    }
    const wrap = this.querySelector('[data-gift-trigger-picker-actions]');
    if (!wrap) return;
    const config = window.giftSystemConfig;
    if (!config || !config.enabled || (config.triggerPicker && config.triggerPicker.enabled === false)) {
      wrap.hidden = true;
      return;
    }
    // Sem API do picker: deixa oculto até o script carregar
    wrap.hidden = true;
  }

  /**
   * Nível atual/próximo/alvo a partir dos níveis configurados e do total
   * elegível do carrinho, do núcleo único (assets/gift-choice-core.js) — a
   * mesma função que a barra da página /cart, o card e o botão de
   * produtos chamam. Sem cópia local do critério de ordenação/seleção.
   */
  resolveTargetLevel(levels, cartTotal) {
    const core = window.GiftChoiceCore;

    if (!core || typeof core.resolveTargetLevel !== 'function') {
      // Núcleo indisponível: nenhum nível é escolhido, a barra some (mesmo
      // efeito de antes quando não havia nível ativo).
      return { currentLevel: null, nextLevel: null, targetLevel: null };
    }

    return core.resolveTargetLevel({ levels, cartTotal });
  }

  /**
   * Progresso completo do gatilho (required/matched/missing/satisfied), do
   * núcleo único (assets/gift-choice-core.js) — a mesma função que motor e
   * card chamam. Alimenta a elegibilidade, o preenchimento proporcional da
   * barra e a mensagem de quantidade faltante.
   */
  resolveTriggerProgress(level, cart) {
    const core = window.GiftChoiceCore;

    if (!core || typeof core.resolveTriggerProgress !== 'function') {
      // Núcleo indisponível: sem gatilho configurado considera satisfeito,
      // como sempre; com gatilho configurado, falha fechado.
      const hasTriggerProducts = Boolean(level && level.triggerProducts && level.triggerProducts.length);
      return {
        required: hasTriggerProducts ? 1 : 0,
        matched: 0,
        missing: hasTriggerProducts ? 1 : 0,
        satisfied: !hasTriggerProducts,
      };
    }

    return core.resolveTriggerProgress({
      cartItems: cart && cart.items,
      triggerProducts: level && level.triggerProducts,
      requiredCount: level && level.triggerMinCount,
      countMode: level && level.triggerCountMode,
    });
  }

  /**
   * Só produto gatilho (sem checar valor). Quantidade e modo de contagem
   * vêm do núcleo único (assets/gift-choice-core.js), a mesma função que o
   * motor e o card chamam. Sem cópia local da regra.
   */
  hasTriggerProductInCart(level, cart) {
    return this.resolveTriggerProgress(level, cart).satisfied;
  }

  /**
   * Verifica se um nível está completamente desbloqueado
   * (valor mínimo + gatilho, na quantidade e modo configurados)
   */
  checkLevelEligibility(level, cart) {
    if (!cart) {
      return false;
    }

    const cartTotal = this.getEligibleCartTotalReais(cart);
    if (cartTotal < level.minValue) {
      return false;
    }

    return this.hasTriggerProductInCart(level, cart);
  }

  /**
   * Limpa event listeners quando o elemento é removido do DOM
   */
  disconnectedCallback() {
    if (
      this.choiceCoordinator &&
      typeof this.choiceCoordinator.unregisterPresenter === 'function'
    ) {
      this.choiceCoordinator.unregisterPresenter(this);
      this.choiceCoordinator = null;
    }

    if (this.boundUpdateProgress) {
      document.removeEventListener('cart-update', this.boundUpdateProgress);
    }
  }
}

if (
  typeof customElements !== 'undefined' &&
  !customElements.get('gift-progress-component')
) {
  customElements.define('gift-progress-component', GiftProgressComponent);
}

